/**
 * This file contains the old static account show view layout code that will eventually be replaced
 * by the new configured grid layout renderer. During migration we will slowly move users over to
 * the new layout via feature flags and eventually remove this file.
 */
import { keyBy } from 'lodash/fp';
import { DateTime } from 'luxon';

import CasesSummary from 'dashboard/components/CasesSummary';
import ConnectedEventTimeline from 'dashboard/components/ConnectedEventTimeline';
import FeatureFlagged from 'dashboard/components/FeatureFlagged';
import ListMatches from 'dashboard/components/ListMatches';
import { RoutedTabbedSection } from 'dashboard/components/TabbedSectionV2';
import TagsSummary from 'dashboard/components/TagsSummary';
import { AccountIdPropertyFormData } from 'dashboard/forms/FormAccountIdProperty';
import { FormUpdateTagsData } from 'dashboard/forms/FormUpdateTags';
import { ColumnContainer, MainColumn, RightColumn } from 'dashboard/layouts/ShowViewLayoutV2';
import { Account } from 'dashboard/models/account';
import { AccountStatus } from 'dashboard/models/account-status';
import { AccountType, AccountTypesFilterOption } from 'dashboard/models/account-type';
import { Case } from 'dashboard/models/case';
import { Device } from 'dashboard/models/device';
import { DocumentGeneric } from 'dashboard/models/document';
import { Environment } from 'dashboard/models/environment';
import { Graph } from 'dashboard/models/graph';
import { GraphQueryTemplate } from 'dashboard/models/graph-query-template';
import { Inquiry } from 'dashboard/models/inquiry';
import { InquiryTemplateFilterOption } from 'dashboard/models/inquiry-template';
import { List } from 'dashboard/models/list';
import { ListItem } from 'dashboard/models/list-item';
import { ListMatch } from 'dashboard/models/list-match';
import { Report } from 'dashboard/models/report';
import { Selfie } from 'dashboard/models/selfie';
import { Tag } from 'dashboard/models/tag';
import { TemplateFilterOption } from 'dashboard/models/template';
import { User } from 'dashboard/models/user';
import { Verification } from 'dashboard/models/verification';
import { AccountState } from 'dashboard/redux/slices/account';
import AccountDevicesSection from 'dashboard/views/DashboardAccountsShowView/components/AccountDevicesSection';
import AccountDocumentsSection from 'dashboard/views/DashboardAccountsShowView/components/AccountDocumentsSection';
import AccountInfo from 'dashboard/views/DashboardAccountsShowView/components/AccountInfo';
import AccountOverviewSection from 'dashboard/views/DashboardAccountsShowView/components/AccountOverviewSection';
import AccountProfileSection from 'dashboard/views/DashboardAccountsShowView/components/AccountProfileSection';
import AccountReportsSection from 'dashboard/views/DashboardAccountsShowView/components/AccountReportsSection';
import AccountSelfiesSection from 'dashboard/views/DashboardAccountsShowView/components/AccountSelfiesSection';
import AccountVerificationsTable from 'dashboard/views/DashboardAccountsShowView/components/AccountVerificationsTable';
import Badge from 'design-system/components/Badge';
import Card from 'design-system/components/Card';
import { TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import { ApiModel, PaginationParams } from 'lib/interfaces/api';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';

export interface AccountDetailsOldProps {
  account: Account;
  accountStatuses: AccountStatus[];
  accountTypes: AccountType[];
  accountTags: Tag[];
  accountTypesFilterOption: AccountTypesFilterOption;
  cases?: Case[];
  currentEnvironment: Environment;
  currentUser: User;
  devices?: Device[];
  documents?: DocumentGeneric[];
  documentPaginationParams: PaginationParams;
  documentPageSize: number;
  featureFlags: FeatureFlagsMap;
  graph: Graph;
  graphQueryTemplateId: string;
  graphQueryTemplates: GraphQueryTemplate[];
  hasBeenRedacted: boolean;
  inquiries?: Inquiry[];
  inquiryTemplateFilterOptions?: (InquiryTemplateFilterOption | TemplateFilterOption)[];
  isLoadingAccount: boolean;
  isLoadingDocuments: boolean;
  isLoadingInquiries: boolean;
  isLoadingReports: boolean;
  isLoadingVerifications: boolean;
  inquiryPageSize: number;
  inquiryPaginationParams: PaginationParams;
  relations: AccountState['relations'];
  reports: Report[];
  reportPaginationParams: PaginationParams;
  reportPageSize: number;
  selfies?: Selfie[];
  users: User[];
  verifications: Verification[];
  verificationPaginationParams: PaginationParams;
  verificationPageSize: number;
  lists: List[];
  listItems: ListItem[];
  listMatches: ListMatch[];
  handleDocumentPageLinkClick: (pageNumber: number) => void;
  handleDocumentPageSizeClick: (pageSize: number) => void;
  handleInquiryPageLinkClick: (pageNumber: number) => void;
  handleInquiryPageSizeClick: (pageSize: number) => void;
  handleReportPageLinkClick: (pageNumber: number) => void;
  handleReportPageSizeClick: (pageSize: number) => void;
  handleVerificationPageLinkClick: (pageNumber: number) => void;
  handleVerificationPageSizeClick: (pageNumber: number) => void;
  handleCreateIdNumber: (values: {
    'identification-number': string;
    'identification-class': string;
    'country-code': string;
  }) => Promise<void>;
  handleUpdateAccountIdNumbers: (values: AccountIdPropertyFormData, index: number) => Promise<void>;
  handleUpdateAccountMainSelfie: (selfieId: string) => Promise<void>;
  handleUpdateTags: (tags: FormUpdateTagsData) => void;
  handleRemoveDevice: (deviceId: string) => Promise<void>;
}

export function AccountDetailsOld({
  account,
  accountStatuses,
  accountTags,
  accountTypes,
  accountTypesFilterOption,
  cases = [],
  currentEnvironment,
  currentUser,
  devices = [],
  documents = [],
  documentPaginationParams,
  documentPageSize,
  featureFlags,
  graph,
  graphQueryTemplateId,
  graphQueryTemplates,
  hasBeenRedacted,
  inquiries = [],
  inquiryTemplateFilterOptions = [],
  isLoadingAccount,
  isLoadingDocuments,
  isLoadingInquiries,
  isLoadingReports,
  isLoadingVerifications,
  inquiryPageSize,
  inquiryPaginationParams,
  reportPaginationParams,
  reportPageSize,
  relations,
  reports = [],
  selfies = [],
  users,
  verifications = [],
  verificationPaginationParams,
  verificationPageSize,
  lists,
  listItems,
  listMatches,
  handleCreateIdNumber,
  handleUpdateTags,
  handleUpdateAccountIdNumbers,
  handleUpdateAccountMainSelfie,
  handleRemoveDevice,
  handleDocumentPageLinkClick,
  handleDocumentPageSizeClick,
  handleInquiryPageLinkClick,
  handleInquiryPageSizeClick,
  handleReportPageLinkClick,
  handleReportPageSizeClick,
  handleVerificationPageLinkClick,
  handleVerificationPageSizeClick,
}: AccountDetailsOldProps) {
  const documentsPaginationEnabled = useFeatureFlag('dashboard:accounts-paginate-documents');

  return (
    <ColumnContainer>
      <MainColumn>
        <RoutedTabbedSection
          tabs={[
            {
              name: 'Overview',
              path: '/',
              contents: (
                <AccountOverviewSection
                  account={account}
                  accountTypes={accountTypes}
                  accountStatuses={accountStatuses}
                  currentUser={currentUser}
                  currentEnvironment={currentEnvironment}
                  inquiries={inquiries}
                  isLoadingInquiries={isLoadingInquiries}
                  inquiryTemplateFilterOptions={inquiryTemplateFilterOptions}
                  graph={graph}
                  graphQueryTemplateId={graphQueryTemplateId}
                  graphQueryTemplates={graphQueryTemplates}
                  featureFlags={featureFlags}
                  inquiryPageSize={inquiryPageSize}
                  inquiryPaginationParams={inquiryPaginationParams}
                  handleInquiryPageLinkClick={handleInquiryPageLinkClick}
                  handleInquiryPageSizeClick={handleInquiryPageSizeClick}
                />
              ),
            },
            {
              name: 'Profile',
              path: '/profile',
              contents: (
                <AccountProfileSection
                  account={account}
                  relations={relations}
                  environmentType={currentEnvironment.type}
                  handleCreateIdNumber={handleCreateIdNumber}
                  handleUpdateAccountIdNumbers={handleUpdateAccountIdNumbers}
                />
              ),
            },
            {
              name: 'Reports',
              badge: <Badge>{reports.length}</Badge>,
              path: '/reports',
              exact: false,
              contents: (
                <AccountReportsSection
                  reports={reports}
                  paginationParams={reportPaginationParams}
                  pageSize={reportPageSize}
                  handlePageLinkClick={handleReportPageLinkClick}
                  handlePageSizeClick={handleReportPageSizeClick}
                  isLoading={isLoadingAccount}
                  isLoadingReports={isLoadingReports}
                />
              ),
            },
            {
              name: 'Verifications',
              badge: <Badge>{verificationPaginationParams?.['total-count']}</Badge>,
              path: '/verifications',
              exact: false,
              contents: (
                <AccountVerificationsTable
                  loading={isLoadingVerifications}
                  paginationParams={verificationPaginationParams}
                  pageSize={verificationPageSize}
                  verifications={verifications}
                  handlePageLinkClick={handleVerificationPageLinkClick}
                  handlePageSizeClick={handleVerificationPageSizeClick}
                />
              ),
            },
            {
              name: 'Documents',
              badge: (
                <Badge>
                  {documentsPaginationEnabled
                    ? documentPaginationParams?.['total-count']
                    : documents.length}
                </Badge>
              ),
              path: '/documents',
              contents: (
                <AccountDocumentsSection
                  isLoading={isLoadingAccount}
                  isLoadingDocuments={isLoadingDocuments}
                  documents={documents}
                  paginationParams={documentPaginationParams}
                  pageSize={documentPageSize}
                  featureFlags={featureFlags}
                  handlePageLinkClick={handleDocumentPageLinkClick}
                  handlePageSizeClick={handleDocumentPageSizeClick}
                />
              ),
            },
            featureFlags['dashboard:accounts-show-account-devices'] && {
              name: 'Devices',
              badge: <Badge>{devices.length}</Badge>,
              path: '/devices',
              contents: (
                <AccountDevicesSection
                  devices={devices}
                  handleRemoveDevice={handleRemoveDevice}
                  isRedacted={hasBeenRedacted}
                />
              ),
            },
            featureFlags['dashboard:decouple-list-matches-from-inquiries'] && {
              name: 'List matches',
              badge: <Badge>{listMatches.length}</Badge>,
              path: '/lists',
              contents: (
                <ListMatches
                  lists={keyBy('id', lists)}
                  listItems={keyBy('id', listItems)}
                  listMatches={keyBy('id', listMatches)}
                />
              ),
            },
            featureFlags['dashboard:accounts-show-account-selfies'] && {
              name: 'Selfies',
              badge: (
                <Badge>
                  {selfies.length + (account.attributes['selfie-photo-url'] != null ? 1 : 0)}
                </Badge>
              ),
              path: '/selfies',
              contents: (
                <AccountSelfiesSection
                  account={account}
                  selfies={selfies}
                  updateMainSelfie={handleUpdateAccountMainSelfie}
                />
              ),
            },
          ].filter(Boolean)}
        />
      </MainColumn>
      <RightColumn>
        <AccountInfo account={account} accountTypeName={accountTypesFilterOption?.name} />
        <TagsSummary
          tags={accountTags}
          tagScope={ApiModel.Account}
          handleUpdateTags={handleUpdateTags}
        />
        <CasesSummary
          cases={cases}
          users={users}
          nullStateText='There are no cases associated with this account.'
        />
        <FeatureFlagged featureFlag='dashboard:accounts-show-event-timeline'>
          <Card>
            <Card.Body>
              <Card.Title>
                Event timeline ({DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})
              </Card.Title>
              <ConnectedEventTimeline
                objects={[account]}
                location='account'
                currentEnvironment={currentEnvironment}
              />
            </Card.Body>
          </Card>
        </FeatureFlagged>
      </RightColumn>
    </ColumnContainer>
  );
}
